'use client';

import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { eventRouter } from '@/shared/infrastructure/events/eventRouter';
// Side-effect import: registers context-specific event routes into the router
import '@/shared/infrastructure/events/registerEventRoutes';

const RECONNECT_DELAY_BASE = 3_000;
const RECONNECT_DELAY_MAX = 60_000;
const MAX_AUTH_FAILURES = 50; // High limit — SSE will keep reconnecting even after auth failures

/** Singleton ref shared between useEventSource and useEventSourceEvent */
let globalEventSource: EventSource | null = null;
let globalLastEventId: string | null = null;
let consecutiveFailures = 0;
const eventListeners = new Map<string, Set<(payload: Record<string, unknown>) => void>>();

/** SSE event types that have been registered on the current EventSource */
const registeredSSETypes = new Set<string>();
let _queryClient: QueryClient | null = null;

// Coalesce bursty SSE-driven cache invalidations. During generation the engine emits
// many events per second, each mapping to query keys; invalidating per event forces a
// refetch storm (observed: ~8 refetches of the same key within 2s, ~140% portal CPU
// and climbing RSS). Batch all invalidations in a short window into ONE refetch per
// distinct key — the UI still updates ~3×/s, smooth, without hammering the server.
const INVALIDATION_WINDOW_MS = 300;
const pendingInvalidations = new Map<string, readonly unknown[]>();
let invalidationTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleInvalidate(qc: QueryClient, key: readonly unknown[]) {
  pendingInvalidations.set(JSON.stringify(key), key);
  if (invalidationTimer) return;
  invalidationTimer = setTimeout(() => {
    const keys = [...pendingInvalidations.values()];
    pendingInvalidations.clear();
    invalidationTimer = null;
    for (const k of keys) qc.invalidateQueries({ queryKey: k });
  }, INVALIDATION_WINDOW_MS);
}

function getGlobalEventSource() {
  return globalEventSource;
}

/**
 * Wire up an addEventListener on the global EventSource for eventType.
 * Safe to call multiple times — deduplicates via registeredSSETypes.
 */
function registerSSEType(es: EventSource, eventType: string, qc: QueryClient) {
  if (registeredSSETypes.has(eventType)) return;
  registeredSSETypes.add(eventType);

  es.addEventListener(eventType, ((e: MessageEvent) => {
    globalLastEventId = (e as MessageEvent & { lastEventId?: string }).lastEventId ?? null;
    const payload = JSON.parse(e.data);

    // Invalidate React Query cache (only for router-registered types), coalesced so a
    // burst of generation events doesn't trigger a per-event refetch storm.
    const keys = eventRouter.getQueryKeys(eventType, payload);
    for (const key of keys) {
      scheduleInvalidate(qc, key);
    }

    // Notify local listeners (useEventSourceEvent / addEventSourceListener)
    eventListeners.get(eventType)?.forEach((handler) => handler(payload));
  }) as EventListener);
}

/** The server stream is open right now: pushed events will arrive (refetch policies read it). */
export function isEventStreamLive(): boolean {
  return !!globalEventSource && globalEventSource.readyState === 1; // EventSource.OPEN
}

/** A local signal (never sent by the server): the stream came back after a drop — events
 *  may have been missed, so whoever keeps state from them re-reads it. */
export const SSE_RECONNECTED = 'sse.reconnected';
/** A local signal: the stream opened (first connect or reconnect). */
export const SSE_CONNECTED = 'sse.connected';

export function addEventSourceListener(
  eventType: string,
  handler: (payload: Record<string, unknown>) => void,
) {
  if (!eventListeners.has(eventType)) {
    eventListeners.set(eventType, new Set());
  }
  eventListeners.get(eventType)!.add(handler);

  // If EventSource is already connected, dynamically register this event type.
  // Force re-register by clearing dedup flag — handles React strict mode remounts.
  if (globalEventSource && _queryClient) {
    registeredSSETypes.delete(eventType);
    registerSSEType(globalEventSource, eventType, _queryClient);
  }

  return () => {
    eventListeners.get(eventType)?.delete(handler);
  };
}

/**
 * Подключается к /api/events/stream один раз на уровне layout.
 * Инвалидирует React Query кеш по маппингу из EventRouter.
 */
export function useEventSource(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;

    _queryClient = queryClient;

    function connect() {
      const url = globalLastEventId
        ? `/api/events/stream?lastEventId=${globalLastEventId}`
        : '/api/events/stream';

      const es = new EventSource(url);
      globalEventSource = es;
      registeredSSETypes.clear();

      // Register all event types from EventRouter
      const routerTypes = eventRouter.getEventTypes();
      for (const eventType of routerTypes) {
        registerSSEType(es, eventType, queryClient);
      }

      // Register any event types that local listeners have subscribed to
      for (const eventType of eventListeners.keys()) {
        registerSSEType(es, eventType, queryClient);
      }

      // Default event type (no named event) — skip, we use named events
      es.onmessage = (e: MessageEvent) => {
        globalLastEventId = (e as MessageEvent & { lastEventId?: string }).lastEventId ?? null;
      };

      es.onopen = () => {
        const wasReconnect = consecutiveFailures > 0;
        consecutiveFailures = 0;
        // reconcile-on-reconnect (SSE audit): the bounded Redis replay buffer can age
        // out events during a long drop (incl. a terminal one). On any RECONNECT, refetch
        // authoritative state so the UI converges even if replay came up short. First
        // connect is skipped — the page load already fetched fresh.
        // every open (the first one too): a screen that read its state before the stream was
        // up may have missed what happened in between — it re-reads (useStoryLive)
        eventListeners.get(SSE_CONNECTED)?.forEach((handler) => handler({}));
        if (wasReconnect) {
          queryClient.invalidateQueries();
          // screens that hold their own state (not React Query) re-read too
          eventListeners.get(SSE_RECONNECTED)?.forEach((handler) => handler({}));
        }
      };

      es.onerror = () => {
        es.close();
        globalEventSource = null;
        registeredSSETypes.clear();

        consecutiveFailures++;
        if (consecutiveFailures >= MAX_AUTH_FAILURES) {
          // Stop reconnecting after too many consecutive failures (likely 401)
          return;
        }

        const delay = Math.min(
          RECONNECT_DELAY_BASE * Math.pow(2, consecutiveFailures - 1),
          RECONNECT_DELAY_MAX,
        );
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      globalEventSource?.close();
      globalEventSource = null;
      registeredSSETypes.clear();
      _queryClient = null;
    };
  }, [queryClient, enabled]);
}
